import Toggle from '@/shared/Inputs/Toggle'
import { useRecoilState } from 'recoil'
import playerAtom, { IPlayerAtomState } from '@/pages/Dashboards/services/player/player.atom.ts'
import DatePicker from '@/shared/Inputs/DatePickers/DatePicker'
import css from './dashboardPlayerControls.module.css'
import Tooltip from 'src/shared/Common/Tooltip'
import ArrowRight from '@/assets/icons/swapRight.svg?react'

const timeIntervals: Array<IPlayerAtomState['selectedTimeInternal']> = ['1m', '5m', '30m', '1h', '12h', '24h', '7d']

const DashboardPlayerControls = () => {
  const [state, setState] = useRecoilState(playerAtom)

  const handleChange = (val: any, name: string) => {
    setState((prev) => ({ ...prev, [name]: val }))
  }

  const handleLiveStateChange = (val) => {
    if (val) {
      setState((prev) => ({ ...prev, offlineTsTo: null, offlineTsFrom: null, live: true }))
    } else {
      setState((prev) => ({ ...prev, live: val }))
      handleTimeIntervalSelected(state.selectedTimeInternal)
    }
  }

  const handleTimeIntervalSelected = (interval: IPlayerAtomState['selectedTimeInternal']) => {
    const prevTsTo = state.offlineTsTo || new Date().getTime()

    switch (interval) {
      case '1m':
        setState((prev) => ({
          ...prev,
          offlineTsTo: prevTsTo,
          offlineTsFrom: prevTsTo - 60 * 1000,
          selectedTimeInternal: interval,
        }))
        break
      case '5m':
        setState((prev) => ({
          ...prev,
          offlineTsTo: prevTsTo,
          offlineTsFrom: prevTsTo - 5 * 60 * 1000,
          selectedTimeInternal: interval,
        }))
        break
      case '30m':
        setState((prev) => ({
          ...prev,
          offlineTsTo: prevTsTo,
          offlineTsFrom: prevTsTo - 30 * 60 * 1000,
          selectedTimeInternal: interval,
        }))
        break
      case '1h':
        setState((prev) => ({
          ...prev,
          offlineTsTo: prevTsTo,
          offlineTsFrom: prevTsTo - 60 * 60 * 1000,
          selectedTimeInternal: interval,
        }))
        break
      case '12h':
        setState((prev) => ({
          ...prev,
          offlineTsTo: prevTsTo,
          offlineTsFrom: prevTsTo - 12 * 60 * 60 * 1000,
          selectedTimeInternal: interval,
        }))
        break
      case '24h':
        setState((prev) => ({
          ...prev,
          offlineTsTo: prevTsTo,
          offlineTsFrom: prevTsTo - 24 * 60 * 60 * 1000,
          selectedTimeInternal: interval,
        }))
        break
      case '7d':
        setState((prev) => ({
          ...prev,
          offlineTsTo: prevTsTo,
          offlineTsFrom: prevTsTo - 7 * 24 * 60 * 60 * 1000,
          selectedTimeInternal: interval,
        }))
        break
      case '30d':
        setState((prev) => ({
          ...prev,
          offlineTsTo: prevTsTo,
          offlineTsFrom: prevTsTo - 30 * 24 * 60 * 60 * 1000,
          selectedTimeInternal: interval,
        }))
    }
  }

  return (
    <div className="inline-flex-gap gap32 center">
      <div className="inline-flex-gap gap8 center" style={{ flexShrink: 0 }}>
        <Toggle name="live" checked={state.live} onChange={handleLiveStateChange} size="large" />
        <p className="text-nm">Онлайн</p>
      </div>
      <div className="inline-flex-gap gap8 center">
        <DatePicker
          isShowTime={true}
          value={state.live ? null : safeDate(state.offlineTsFrom)}
          disabled={state.live}
          style={{ width: '200px' }}
          type="date"
          onChange={(date) => {
            handleChange(date[0].getTime(), 'offlineTsFrom')
          }}
        />
        <ArrowRight style={{ flexShrink: 0 }} />
        <DatePicker
          isShowTime={true}
          value={state.live ? null : safeDate(state.offlineTsTo)}
          disabled={state.live}
          style={{ width: '200px' }}
          type="date"
          onChange={(date) => {
            handleChange(date[0].getTime(), 'offlineTsTo')
          }}
        />
      </div>
      <Tooltip text={`Выбор кадра доступен только в режиме "Онлайн"`} active={!state.live} position="bottom">
        <div
          className="inline-flex-gap gap4"
          style={{ opacity: state.live ? 'unset' : '0.5', cursor: state.live ? 'unset' : 'not-allowed' }}
        >
          {timeIntervals.map((interval) => (
            <PlayerControlInterval
              key={interval}
              interval={interval}
              onSelect={handleTimeIntervalSelected}
              active={state.selectedTimeInternal === interval}
              disabled={!state.live}
            />
          ))}
        </div>
      </Tooltip>
    </div>
  )
}

interface IPlayerControlTimeIntervalProps {
  interval: IPlayerAtomState['selectedTimeInternal']
  onSelect: (interval: IPlayerAtomState['selectedTimeInternal']) => void
  active: boolean
  disabled: boolean
}

const PlayerControlInterval = (props: IPlayerControlTimeIntervalProps) => {
  return (
    <div
      className={css.controlWrapper}
      data-disabled={props.disabled}
      data-active={props.active}
      onClick={() => !props.disabled && props.onSelect(props.interval)}
    >
      <p className="text-sm" style={{ fontWeight: '500', color: 'inherit' }}>
        {props.interval}
      </p>
    </div>
  )
}

const safeDate = (value: string | number) => {
  try {
    const dt = new Date(value)
    if (dt.toString() === 'Invalid Date') {
      return new Date()
    }

    return dt
  } catch (_) {
    return new Date()
  }
}

export default DashboardPlayerControls
